import React, { createContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getAuthData,
  setAuthData,
  setAuthoritiesData,
} from "../utils/LocalStorageManager";
import jwt_decode from "jwt-decode";

const AUTH_SYNC_STORAGE_KEY = "sinpepayAuthSyncEvent";
/*****************************************************************************
 * REVISION 1.0
 * Fecha: 06/06/2024
 *
 * @description Conmtexto principal de la app
 * @author luis.ferrer
 * @version 1.0
 *
 ****************************************************************************/
const AppContext = createContext();
const { Provider } = AppContext;

const AppProvider = ({ children, authData, authOptionData }) => {
  const [authInfo, setAuthInfo] = useState(authData);
  const [authOption, setAuthOption] = useState(authOptionData);
  const [sesionExp, setSesionExp] = useState(0);
  const [isLoading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [warningMsg, setWarningMsg] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [modalData, setModalData] = useState({
    modalOpen: false,
    modalType: null,
    modalObject: null,
  });

  
  const navigate = useNavigate();

  const notifyAuthSync = () => {
    window.localStorage.setItem(
      AUTH_SYNC_STORAGE_KEY,
      JSON.stringify({ updatedAt: Date.now() })
    );
  };

  const syncAuthStateFromStorage = async () => {
    const authObject = await getAuthData();
    const storageAuthInfo = authObject?.authInfo;

    if (!storageAuthInfo?.access_token) {
      setAuthInfo(null);
      setAuthOption([]);
      setAuthenticate(false);
      setSesionExp(0);
      return;
    }

    try {
      const decodeToken = jwt_decode(storageAuthInfo.access_token);
      const expiresAt = decodeToken?.exp;
      const valid = (new Date().getTime() / 1000) < expiresAt;

      if (!valid) {
        setAuthInfo(null);
        setAuthOption([]);
        setAuthenticate(false);
        setSesionExp(0);
        return;
      }

      const currentTime = Math.floor(Date.now() / 1000);

      setAuthInfo(storageAuthInfo);
      setAuthOption(authObject?.authOption || []);
      setAuthenticate(true);

      setSesionExp(
        currentTime + Number(storageAuthInfo?.timeSession || 1800)
      );

    } catch (error) {
      setAuthInfo(null);
      setAuthOption([]);
      setAuthenticate(false);
      setSesionExp(0);
    }
  };

  const [authenticate, setAuthenticate] = useState(() => {
    if (!authData) {
      return false;
    }

    const decodeToken = jwt_decode(authData?.access_token);
    const expiresAt = decodeToken?.exp;
    let valid = (new Date().getTime() / 1000) < expiresAt;

    if (valid) {

      const currentTime = Math.floor(Date.now() / 1000);

      setSesionExp(
        currentTime + Number(authData?.timeSession || 1800)
      );

      return true;
    }

    return false;
  });

  useEffect(() => {
    const handleStorage = async (event) => {
      if (event.key === AUTH_SYNC_STORAGE_KEY) {
        await syncAuthStateFromStorage();
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("storage", handleStorage);
    };
    // eslint-disable-next-line
  }, []);

  const loginProvider = async (loginData, redirect = true) => {
    try {
    
      //decode token
      const decodeToken = jwt_decode(loginData?.access_token);
  
      const expiresAt = decodeToken?.exp;
      let valid = (new Date().getTime() / 1000) < expiresAt;

      if (valid) {

        const currentTime = Math.floor(Date.now() / 1000);

        // Tiempo de sesión configurado por plan
        setSesionExp(
          currentTime + Number(loginData?.timeSession || 1800)
        );

        //set user data
        const authData = await setAuthData("authInfo", {
          ...loginData,
          username: decodeToken?.user_name,
          ic: decodeToken?.ic,
          ioc: decodeToken?.ioc,
        });

        if (!authData) {
          setAuthenticate(false);
          return;
        }

        setAuthenticate(valid);
        setAuthInfo(authData);

        //set options
        const optionData = await setAuthoritiesData(
          "authOption",
          decodeToken?.authorities
        );

        if (optionData) {
          setAuthOption(optionData);
        }

        //Change password
        if (authData.indPw === "S") {
          navigate("/board/profile", { replace: true });
        } else if (authData.orgPlan === "LITE") {
          navigate("/board/search/sinpe", { replace: true });
        } else if (redirect) {
          navigate("/board/search/transaction", { replace: true });
        }

        notifyAuthSync();
        return;
      }
    } catch (error) {
      console.log(error);
    }

    setAuthenticate(false);
  };

  const logoutProvider = async (session) => {
    await setAuthData("authInfo", null);
    await setAuthoritiesData("authOption", null);
    notifyAuthSync();
    setAuthenticate(false);
    navigate(`/${session ? "?session=logout" : ""}`, { replace: true });
  };

  const resetErrorMsg = () => {
    setErrorMsg(null);
  };

  const resetWarningMsg = () => {
    setWarningMsg(null);
  };

  const resetSuccessMsg = () => {
    setSuccessMsg(null);
  };

  const resetAllMsg = () => {
    setErrorMsg(null);
    setWarningMsg(null);
    setSuccessMsg(null);
  };

  const values = useMemo(
    () => ({
      authInfo,
      authOption,
      authenticate,
      sesionExp,
      isLoading,
      errorMsg,
      warningMsg,
      successMsg,
      modalData,      
      loginProvider,
      logoutProvider,
      setLoading,
      resetErrorMsg,
      setErrorMsg,
      resetSuccessMsg,
      setWarningMsg,
      resetWarningMsg,
      setSuccessMsg,
      resetAllMsg,
      setModalData,
    }),
    // eslint-disable-next-line
    [authInfo, authenticate, sesionExp, isLoading, errorMsg, warningMsg, successMsg, modalData]
  );

  return <Provider value={values}>{children}</Provider>;
};

export { AppContext, AppProvider };
